import { EventType, KnownMembership, MatrixEvent, RoomStateEvent } from 'matrix-js-sdk/src';
import { useCallback, useEffect, useState } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { getUserPowerLevel } from 'utils/matrixUtils';

const useUserPowerLevel = (roomId: string | null) => {
  const { matrixClient } = useMatrixContext();

  const getPowerLevel = useCallback(() => {
    const userId = matrixClient.getUserId();
    const room = roomId ? matrixClient.getRoom(roomId) : null;
    if (!room || !userId) return 0;
    // Guard: only expose admin power level once the server has confirmed the join.
    // While the local user's membership is still 'invite' (after joinRoom() returns
    // but before /sync arrives), return 0 to avoid showing stale admin UI.
    if (room.getMyMembership() !== KnownMembership.Join) return 0;

    return getUserPowerLevel(room, userId);
  }, [matrixClient, roomId]);

  const [powerLevel, setPowerLevel] = useState(getPowerLevel);

  useEffect(() => setPowerLevel(getPowerLevel()), [getPowerLevel]);

  useEffect(() => {
    if (!roomId) return;

    const handleRoomStateEvent = (event: MatrixEvent) => {
      if (event.getRoomId() !== roomId) return;
      if (event.getType() === EventType.RoomPowerLevels) setPowerLevel(getPowerLevel());
    };

    matrixClient.on(RoomStateEvent.Events, handleRoomStateEvent);

    return () => {
      matrixClient.off(RoomStateEvent.Events, handleRoomStateEvent);
    };
  }, [matrixClient, roomId, getPowerLevel]);

  return powerLevel;
};

export default useUserPowerLevel;
